// Angular imports
import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

// 3rd party imports
import { DxValidationGroupComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';
import { TextBoxType } from 'devextreme/ui/text_box';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';

// CC-Common imports
import { IconConstants } from '@therealmanage/ciranet-ui-common';

// Service imports
import { ScreenService } from '../../_services/shared-services/screen.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { QuickPayAuthService } from './services/quick-pay-auth.service';

// Model imports
import { LoginParameterDto } from '../../_models/login-parameter-dto';
import { QuickPaymentPropertiesResultDto } from '../../_models/quick-payment-properties-result-dto';

// Constant imports
import { MessageInterval } from '../../_constants/messages-intervals';
import { ValidationFormatConstants } from '../../_constants/validation-format-constants';
import { ImageConstants } from '../../_constants/image-constants';

// Local constants
import {
  QuickPaymentViewMode,
  QuickPayErrorMessages,
  QuickPayErrorCodes,
  QuickPayAuthMethod,
  QuickPayStorageKeys,
  QuickPayPropertyState
} from './constants/quick-pay.constants';

/**
 * Quick Payment Component
 *
 * Provides multiple authentication flows for quick payment:
 * - Credentials (email/password)
 * - Google OAuth
 * - Account Number (16-digit)
 *
 * Security: Clears auth state immediately after fetching property data
 * to prevent users from accessing the portal dashboard.
 */
@Component({
  selector: 'app-quick-payment',
  templateUrl: './quick-payment.component.html',
  styleUrls: ['./quick-payment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class QuickPaymentComponent implements OnInit, OnDestroy {
  @Input() isEmbedded = false;

  @ViewChild('validatorLogin', { static: false })
  validatorLogin?: DxValidationGroupComponent;

  @ViewChild('validateAccountNumber', { static: false })
  validateAccountNumber?: DxValidationGroupComponent;

  // UI State
  isSmallScreen = false;
  loadingVisible = false;
  errorMessage = '';
  isAccountInfoShow = false;
  showPropertySelection = false;
  showGoogleAuth = true;

  // Form State
  passwordMode: TextBoxType = 'password';
  loginParam: LoginParameterDto = new LoginParameterDto();
  accountNumber = '';
  currentViewMode: QuickPaymentViewMode = 'account-number';

  // Property Selection State
  quickPaymentProperties: QuickPaymentPropertiesResultDto[] = [];
  hasProperties = true;
  selectedCustomerId: string | null = null;

  // Validation patterns
  readonly accountNumberPattern =
    ValidationFormatConstants.accountNumberPattern;
  readonly emailPattern: RegExp = ValidationFormatConstants.emailPattern;

  // Dynamic content - set based on auth method
  noPropertyMessage: string = QuickPayErrorMessages.ONLINE_PAYMENT_UNAVAILABLE;
  readonly helpImage = ImageConstants.helpAccountImage;
  readonly backToLoginIcon = IconConstants.BACK_TO_LOGIN;
  readonly proceedIcon = IconConstants.PROCEED;

  // Password toggle button config (simplified - single object)
  readonly passwordButtonOptions = {
    type: 'default',
    stylingMode: 'text',
    onClick: () => this.togglePasswordVisibility()
  };

  // Subscription cleanup
  private readonly destroy$ = new Subject<void>();

  // Double-click prevention
  private isProcessing = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private screenService: ScreenService,
    private errorService: ServerErrorService,
    private helperService: HelperService,
    private quickPayAuth: QuickPayAuthService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Redirect direct route access to login page with Quick Pay expanded
    // Quick Pay should only render in the embedded Quick Pay card, not in router-outlet
    if (!this.isEmbedded) {
      this.router.navigate(['user', 'login'], {
        queryParams: { quickPaySuccess: 'true' },
        replaceUrl: true
      });
      return;
    }

    this.initializeScreenListener();
    this.handleGoogleOAuthReturn();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================================
  // Password Visibility
  // ============================================================

  get passwordButtonIcon(): string {
    return this.passwordMode === 'password'
      ? 'fa-solid fa-eye'
      : 'fa-solid fa-eye-slash';
  }

  get passwordButtonHint(): string {
    return this.passwordMode === 'password' ? 'Show Password' : 'Hide Password';
  }

  togglePasswordVisibility(): void {
    this.passwordMode = this.passwordMode === 'password' ? 'text' : 'password';
    this.cdr.markForCheck();
  }

  // ============================================================
  // View Mode Switching
  // ============================================================

  switchToAccountNumber(): void {
    this.currentViewMode = 'account-number';
    this.clearCredentialsForm();
    this.cdr.markForCheck();
  }

  switchToCredentials(): void {
    this.currentViewMode = 'credentials';
    this.clearAccountNumberForm();
    this.cdr.markForCheck();
  }

  // ============================================================
  // Navigation
  // ============================================================

  backToLogin(): void {
    this.router.navigate(['user', 'login']);
  }

  goBack(): void {
    this.showPropertySelection = false;
    this.quickPaymentProperties = [];
    this.selectedCustomerId = null;
    this.errorMessage = '';
    this.cdr.markForCheck();
  }

  // ============================================================
  // Property Selection
  // ============================================================

  selectProperty(customerId: string): void {
    this.selectedCustomerId = customerId;
    this.cdr.markForCheck();
  }

  confirmSingleProperty(): void {
    if (this.quickPaymentProperties.length === 1) {
      this.navigateToPayment(this.quickPaymentProperties[0]);
    }
  }

  onConfirm(): void {
    if (!this.selectedCustomerId) {
      notify(
        QuickPayErrorMessages.NO_PROPERTY_SELECTED,
        'error',
        MessageInterval.defaultErrorMsgInterval
      );
      return;
    }

    const selectedProperty = this.quickPaymentProperties.find(
      (p) => p.customerId === this.selectedCustomerId
    );

    if (selectedProperty) {
      this.navigateToPayment(selectedProperty);
    }
  }

  // ============================================================
  // Authentication Entry Point
  // ============================================================

  /**
   * Main entry point for payment flow.
   * Handles both credentials and account number authentication.
   * Includes double-click prevention.
   */
  proceedToPayment(): void {
    // Prevent double-clicks
    if (this.isProcessing || this.loadingVisible) {
      return;
    }

    if (this.currentViewMode === 'credentials') {
      this.handleCredentialsFlow();
    } else {
      this.handleAccountNumberFlow();
    }
  }

  // ============================================================
  // Google OAuth
  // ============================================================

  onGoogleSignInClick(): void {
    if (this.isProcessing) {
      return;
    }
    this.quickPayAuth.initiateGoogleOAuth();
  }

  // ============================================================
  // UI Helpers
  // ============================================================

  showAccountInfo(event: Event): void {
    event.preventDefault();
    this.isAccountInfoShow = true;
    this.cdr.markForCheck();
  }

  /**
   * Track function for ngFor to improve performance
   */
  trackByPropertyId(
    _index: number,
    property: QuickPaymentPropertiesResultDto
  ): string {
    return property.customerId;
  }

  // ============================================================
  // Private Methods - Credentials Flow
  // ============================================================

  private handleCredentialsFlow(): void {
    // If already logged in (e.g., returning from previous attempt), skip login
    if (this.quickPayAuth.isAuthenticated) {
      this.setLoading(true);
      this.fetchUserProperties(QuickPayAuthMethod.CREDENTIALS);
      return;
    }

    // Validate form
    if (!this.validateCredentialsForm()) {
      return;
    }

    // Perform login
    this.setLoading(true);
    this.isProcessing = true;
    this.errorMessage = '';

    this.quickPayAuth
      .loginWithCredentials(this.loginParam)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isProcessing = false;
        })
      )
      .subscribe({
        next: () => {
          // Login successful - fetch properties (loading stays visible)
          this.fetchUserProperties(QuickPayAuthMethod.CREDENTIALS);
        },
        error: (error) => {
          this.handleLoginError(error);
        }
      });
  }

  private validateCredentialsForm(): boolean {
    const hasEmail = this.loginParam.username?.trim().length > 0;
    const hasPassword = this.loginParam.password?.trim().length > 0;

    // Trigger validation to show inline errors
    if (!hasEmail || !hasPassword) {
      this.validatorLogin?.instance?.validate();
      return false;
    }

    const validationResult = this.validatorLogin?.instance?.validate();
    return validationResult?.isValid ?? false;
  }

  private handleLoginError(error: any): void {
    this.setLoading(false);

    if (error.status === 404) {
      this.errorMessage = QuickPayErrorMessages.INVALID_CREDENTIALS;

      if (!this.isEmbedded) {
        this.errorService.updateSubjectErrorMessage(
          'Login failed - Username or Password is invalid!'
        );
      }

      // Offer fallback to account number if available
      if (this.accountNumber?.trim()) {
        this.offerAccountNumberFallback();
      }
    } else {
      if (!this.isEmbedded) {
        this.errorService.displayError(error);
      } else {
        this.errorMessage = QuickPayErrorMessages.INVALID_CREDENTIALS;
      }
    }

    this.cdr.markForCheck();
  }

  private offerAccountNumberFallback(): void {
    const dialog = this.helperService.getConfirmationDialog(
      'Warning?',
      'Do you want to continue with just account number?',
      'Yes',
      'No',
      'default',
      'default'
    );

    dialog.show().then((answer) => {
      if (answer === true) {
        this.handleAccountNumberFlow();
      }
    });
  }

  // ============================================================
  // Private Methods - Account Number Flow
  // ============================================================

  private handleAccountNumberFlow(): void {
    if (!this.validateAccountNumberForm()) {
      return;
    }

    this.setLoading(true);
    this.isProcessing = true;
    this.errorMessage = '';
    this.hasProperties = true;

    this.quickPayAuth
      .fetchPropertiesByAccountNumber(this.accountNumber)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isProcessing = false;
          this.setLoading(false);
        })
      )
      .subscribe({
        next: (properties) => {
          this.handlePropertiesReceived(
            properties,
            QuickPayAuthMethod.ACCOUNT_NUMBER
          );
        },
        error: (error) => {
          this.handleAccountNumberError(error);
        }
      });
  }

  private validateAccountNumberForm(): boolean {
    if (!this.accountNumber?.trim()) {
      this.validateAccountNumber?.instance?.validate();
      return false;
    }

    const validationResult = this.validateAccountNumber?.instance?.validate();
    return validationResult?.isValid ?? false;
  }

  private handleAccountNumberError(error: any): void {
    if (error.status === 403 || error.status === 400) {
      const serverMessage = error.error?.message;

      if (serverMessage && !this.isEmbedded) {
        this.errorService.displayError(error);
      } else if (!serverMessage && !this.isEmbedded) {
        const defaultMsg =
          error.status === 403
            ? QuickPayErrorMessages.ONLINE_PAYMENT_UNAVAILABLE
            : 'Unable to retrieve property information. Please verify your account number.';
        this.errorService.updateSubjectErrorMessage(defaultMsg);
      }

      this.errorMessage = QuickPayErrorMessages.INVALID_ACCOUNT_NUMBER;
    } else {
      if (!this.isEmbedded) {
        this.errorService.displayError(error);
      }
      this.errorMessage = QuickPayErrorMessages.INVALID_ACCOUNT_NUMBER;
    }

    this.cdr.markForCheck();
  }

  // ============================================================
  // Private Methods - Google OAuth
  // ============================================================

  private handleGoogleOAuthReturn(): void {
    // Check for error query params (use predefined codes to prevent XSS)
    const quickPayError = this.route.snapshot.queryParams['quickPayError'];

    // Handle Google SSO failure
    if (quickPayError === QuickPayErrorCodes.GOOGLE_SSO_FAILED) {
      this.errorMessage = QuickPayErrorMessages.GOOGLE_SIGN_IN_FAILED;
      this.cdr.markForCheck();
      return;
    }

    // Handle no properties error (redirected from Google callback)
    if (quickPayError === QuickPayErrorCodes.NO_PROPERTIES) {
      this.errorMessage = QuickPayErrorMessages.NO_PROPERTIES_FOUND;
      this.cdr.markForCheck();
      return;
    }

    // Check for pre-fetched properties from Google OAuth flow
    // GoogleCallbackComponent fetches properties and clears auth BEFORE navigating here
    const storedProperties = sessionStorage.getItem(
      QuickPayStorageKeys.GOOGLE_AUTH_PROPERTIES
    );

    if (storedProperties) {
      try {
        const properties: QuickPaymentPropertiesResultDto[] =
          JSON.parse(storedProperties);

        // Clean up session storage
        sessionStorage.removeItem(QuickPayStorageKeys.GOOGLE_AUTH_PROPERTIES);

        // Track successful auth
        this.quickPayAuth.trackAuthEvent(
          'quick_payment_auth_success',
          QuickPayAuthMethod.GOOGLE
        );

        // Display the pre-fetched properties
        this.handlePropertiesReceived(properties, QuickPayAuthMethod.GOOGLE);
      } catch {
        // Invalid data in session storage - show error
        sessionStorage.removeItem(QuickPayStorageKeys.GOOGLE_AUTH_PROPERTIES);
        this.errorMessage = QuickPayErrorMessages.GOOGLE_SIGN_IN_FAILED;
        this.cdr.markForCheck();
      }
    }
  }

  // ============================================================
  // Private Methods - Property Fetching (Authenticated Flow)
  // ============================================================

  private fetchUserProperties(authMethod: string): void {
    this.setLoading(true);
    this.errorMessage = '';
    this.hasProperties = true;

    this.quickPayAuth
      .fetchAuthenticatedUserProperties()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.setLoading(false);
        })
      )
      .subscribe({
        next: (properties) => {
          this.handlePropertiesReceived(properties, authMethod);
        },
        error: (error) => {
          this.quickPayAuth.trackAuthEvent(
            'quick_payment_auth_failed',
            authMethod,
            error?.error?.message || 'Failed to fetch properties'
          );

          if (!this.isEmbedded) {
            this.errorService.displayError(error);
          }
          this.errorMessage =
            QuickPayErrorMessages.UNABLE_TO_RETRIEVE_PROPERTIES;
          this.cdr.markForCheck();
        }
      });
  }

  // ============================================================
  // Private Methods - Property Handling (Shared)
  // ============================================================

  private handlePropertiesReceived(
    properties: QuickPaymentPropertiesResultDto[],
    authMethod: string = QuickPayAuthMethod.ACCOUNT_NUMBER
  ): void {
    this.quickPaymentProperties = properties;
    this.hasProperties = properties.length > 0;

    // Handle no properties case for authenticated users
    if (!this.hasProperties) {
      if (
        authMethod === QuickPayAuthMethod.CREDENTIALS ||
        authMethod === QuickPayAuthMethod.GOOGLE
      ) {
        // If standalone (not embedded), redirect to login page with error
        // The Quick Pay component on login page will display the error
        if (!this.isEmbedded) {
          this.router.navigate(['user', 'login'], {
            queryParams: { quickPayError: QuickPayErrorCodes.NO_PROPERTIES }
          });
          return;
        }
        // If embedded, show error message directly
        this.errorMessage = QuickPayErrorMessages.NO_PROPERTIES_FOUND;
        this.cdr.markForCheck();
        return;
      }
      // Account number lookup - community may not support online payment
      // Show property selection UI with no properties message
      this.noPropertyMessage = QuickPayErrorMessages.ONLINE_PAYMENT_UNAVAILABLE;
    }

    if (this.hasProperties && properties.length === 1) {
      // Auto-select single property
      this.selectedCustomerId = properties[0].customerId;
    }

    // Show property selection UI
    this.showPropertySelection = true;
    this.cdr.markForCheck();
  }

  // ============================================================
  // Private Methods - Navigation
  // ============================================================

  /**
   * Navigates to Quick Pay payment page.
   * Uses route state to pass property data (not query params) to avoid PII in URL.
   */
  private navigateToPayment(property: QuickPaymentPropertiesResultDto): void {
    // Store property data in session storage for the payment page
    // This avoids PII (property address) appearing in URL/browser history
    const propertyState: QuickPayPropertyState = {
      communityId: property.communityId,
      customerId: property.customerId,
      communityName: property.communityName,
      propertyAddress: property.propertyAddress
    };

    sessionStorage.setItem(
      QuickPayStorageKeys.SELECTED_PROPERTY,
      JSON.stringify(propertyState)
    );

    // Navigate with minimal query params (no PII)
    this.router.navigate([
      property.communityId,
      property.customerId,
      'one-time-payment'
    ]);
  }

  // ============================================================
  // Private Methods - Form Clearing
  // ============================================================

  private clearCredentialsForm(): void {
    this.errorMessage = '';
    this.loginParam.username = '';
    this.loginParam.password = '';
    this.validatorLogin?.instance?.reset();
  }

  private clearAccountNumberForm(): void {
    this.errorMessage = '';
    this.accountNumber = '';
    this.validateAccountNumber?.instance?.reset();
  }

  // ============================================================
  // Private Methods - UI State
  // ============================================================

  private setLoading(visible: boolean): void {
    this.loadingVisible = visible;
    this.cdr.markForCheck();
  }

  private initializeScreenListener(): void {
    this.updateScreenSize();
    this.screenService.changed.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateScreenSize();
      this.cdr.markForCheck();
    });
  }

  private updateScreenSize(): void {
    this.isSmallScreen = this.screenService.isSmallScreen();
  }
}
